import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart3,
  Clock,
  CheckSquare,
  FolderKanban,
  TrendingUp,
  Users,
  AlertTriangle,
  Calendar,
} from 'lucide-react'
import { reportsApi, clientsApi } from '@/services/api'
import { cn } from '@/lib/utils'
import { StatCard } from '@/components/common/StatCard'
import type { Client } from '@/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ReportType = 'projects' | 'clients' | 'tasks'

interface ReportFilters {
  type: ReportType
  startDate: string
  endDate: string
  clientId: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDefaultDates(): { startDate: string; endDate: string } {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  }
}

const REPORT_TABS: { value: ReportType; label: string }[] = [
  { value: 'projects', label: 'Progetti' },
  { value: 'clients', label: 'Clienti' },
  { value: 'tasks', label: 'Task' },
]

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SimpleBarChart({ data, unit = '' }: { data: { label: string; value: number; max: number }[]; unit?: string }) {
  return (
    <div className="space-y-3">
      {data.map((item) => (
        <div key={item.label} className="flex items-center gap-3">
          <span className="text-sm text-slate-600 w-40 truncate flex-shrink-0" title={item.label}>
            {item.label}
          </span>
          <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-primary-500 h-full rounded-full transition-all duration-500"
              style={{ width: item.max > 0 ? `${(item.value / item.max) * 100}%` : '0%' }}
            />
          </div>
          <span className="text-sm font-medium text-slate-700 w-16 text-right flex-shrink-0 tabular-nums">
            {item.value}{unit}
          </span>
        </div>
      ))}
    </div>
  )
}

function StatusBreakdown({ items }: { items: { label: string; count: number; color: string }[] }) {
  const total = items.reduce((s, i) => s + i.count, 0)
  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-3">
          <div className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', item.color)} />
          <span className="text-sm text-slate-600 flex-1">{item.label}</span>
          <span className="text-sm font-semibold text-slate-900 tabular-nums">{item.count}</span>
          <span className="text-xs text-slate-400 w-10 text-right tabular-nums">
            {total > 0 ? `${Math.round((item.count / total) * 100)}%` : '0%'}
          </span>
        </div>
      ))}
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-2xl shadow-card p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-7 w-7 bg-slate-100 rounded-lg animate-pulse" />
              <div className="h-3 w-24 bg-slate-100 rounded animate-pulse" />
            </div>
            <div className="h-7 w-16 bg-slate-100 rounded animate-pulse mb-2" />
            <div className="h-3 w-20 bg-slate-100 rounded animate-pulse" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-2xl shadow-card p-5 h-64 animate-pulse" />
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Report Views
// ---------------------------------------------------------------------------

function ProjectsReport({ data }: { data: any }) {
  const statusBreakdown: Record<string, number> = data?.statusBreakdown ?? {}
  const total: number = data?.totalProjects ?? Object.values(statusBreakdown).reduce((s, v) => s + (v as number), 0)
  // Clamp derived KPI a 0 quando non ci sono progetti nel periodo, per evitare
  // valori contraddittori (es. Totale 0 ma In Budget 1) dovuti a conteggi backend
  // calcolati su perimetri diversi.
  const completionRate: number = total > 0 ? (data?.completionRate ?? 0) : 0
  const onBudget: number = total > 0 ? (data?.onBudgetCount ?? 0) : 0
  const overBudget: number = total > 0 ? (data?.overBudgetCount ?? 0) : 0

  const STATUS_LABELS: Record<string, string> = {
    DRAFT: 'Bozza',
    PLANNING: 'Pianificazione',
    ACTIVE: 'Attivo',
    ON_HOLD: 'In Pausa',
    COMPLETED: 'Completato',
    CANCELLED: 'Annullato',
  }
  const STATUS_COLORS: Record<string, string> = {
    DRAFT: 'bg-slate-400',
    PLANNING: 'bg-primary-400',
    ACTIVE: 'bg-success-500',
    ON_HOLD: 'bg-warning-500',
    COMPLETED: 'bg-success-500',
    CANCELLED: 'bg-danger-400',
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FolderKanban} label="Progetti Totali" value={total} hint="Nel periodo" tone="primary" />
        <StatCard icon={TrendingUp} label="Tasso Completamento" value={`${completionRate}%`} hint="Completati vs totali" tone="success" />
        <StatCard icon={CheckSquare} label="In Budget" value={onBudget} hint="Progetti nei costi" tone="neutral" />
        <StatCard icon={AlertTriangle} label="Fuori Budget" value={overBudget} hint="Progetti oltre il budget" tone="danger" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Distribuzione per Stato</h2>
          {Object.keys(statusBreakdown).length > 0 ? (
            <StatusBreakdown
              items={Object.entries(statusBreakdown).map(([key, count]) => ({
                label: STATUS_LABELS[key] ?? key,
                count: count as number,
                color: STATUS_COLORS[key] ?? 'bg-slate-400',
              }))}
            />
          ) : (
            <div className="flex items-center justify-center h-32 text-sm text-slate-400">Nessun dato disponibile</div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Tasso di Completamento</h2>
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="relative w-28 h-28">
              <svg className="w-28 h-28 -rotate-90" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="32" fill="none" stroke="#E6E8EC" strokeWidth="8" />
                <circle
                  cx="40" cy="40" r="32" fill="none"
                  stroke="#00A1FF" strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 32}`}
                  strokeDashoffset={`${2 * Math.PI * 32 * (1 - completionRate / 100)}`}
                  className="transition-all duration-700"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-semibold text-slate-900 tabular-nums">{completionRate}%</span>
              </div>
            </div>
            <p className="text-sm text-slate-500">dei progetti sono stati completati</p>
          </div>
        </div>
      </div>
    </div>
  )
}

function ClientsReport({ data }: { data: any }) {
  const statusDist: Record<string, number> = data?.statusDistribution ?? {}
  const healthDist: Record<string, number> = data?.healthScoreDistribution ?? {}
  const topClients: Array<{ name: string; activityCount: number }> = data?.topClientsByActivity ?? []

  const STATUS_LABELS: Record<string, string> = {
    PROSPECT: 'Prospect',
    ACTIVE: 'Attivo',
    INACTIVE: 'Inattivo',
    CHURNED: 'Perso',
  }
  const STATUS_COLORS: Record<string, string> = {
    PROSPECT: 'bg-primary-400',
    ACTIVE: 'bg-success-500',
    INACTIVE: 'bg-slate-400',
    CHURNED: 'bg-danger-400',
  }
  const maxActivity = Math.max(...topClients.map((c) => c.activityCount), 1)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Clienti Totali" value={data?.totalClients ?? 0} hint="Nel sistema" tone="primary" />
        <StatCard icon={TrendingUp} label="Clienti Attivi" value={statusDist['ACTIVE'] ?? 0} hint="Status ACTIVE" tone="success" />
        <StatCard icon={AlertTriangle} label="Prospect" value={statusDist['PROSPECT'] ?? 0} hint="Da convertire" tone="warning" />
        <StatCard icon={CheckSquare} label="Health Score Medio" value={`${data?.avgHealthScore ?? 0}`} hint="Su scala 0-100" tone="neutral" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Distribuzione per Stato</h2>
          {Object.keys(statusDist).length > 0 ? (
            <StatusBreakdown
              items={Object.entries(statusDist).map(([k, v]) => ({
                label: STATUS_LABELS[k] ?? k,
                count: v as number,
                color: STATUS_COLORS[k] ?? 'bg-slate-400',
              }))}
            />
          ) : (
            <div className="text-sm text-slate-400 text-center py-8">Nessun dato</div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Distribuzione Health Score</h2>
          {Object.keys(healthDist).length > 0 ? (
            <StatusBreakdown
              items={Object.entries(healthDist).map(([k, v]) => ({
                label: k,
                count: v as number,
                color: k === 'Ottimo' ? 'bg-success-500' : k === 'Buono' ? 'bg-primary-400' : k === 'Sufficiente' ? 'bg-warning-500' : 'bg-danger-400',
              }))}
            />
          ) : (
            <div className="text-sm text-slate-400 text-center py-8">Nessun dato</div>
          )}
        </div>
      </div>

      {topClients.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Top Clienti per Attivita'</h2>
          <SimpleBarChart
            data={topClients.map((c) => ({ label: c.name, value: c.activityCount, max: maxActivity }))}
            unit=" att."
          />
        </div>
      )}
    </div>
  )
}

function TasksReport({ data }: { data: any }) {
  const statusBreakdown: Record<string, number> = data?.statusBreakdown ?? {}
  const priorityDist: Record<string, number> = data?.priorityDistribution ?? {}
  const overdueCount: number = data?.overdueCount ?? 0
  const completionRate: number = data?.completionRate ?? 0
  const total: number = data?.totalTasks ?? 0

  const STATUS_LABELS: Record<string, string> = {
    TODO: 'Da fare',
    IN_PROGRESS: 'In corso',
    IN_REVIEW: 'In review',
    BLOCKED: 'Bloccato',
    DONE: 'Completato',
    CANCELLED: 'Annullato',
  }
  const STATUS_COLORS: Record<string, string> = {
    TODO: 'bg-slate-400',
    IN_PROGRESS: 'bg-primary-400',
    IN_REVIEW: 'bg-warning-400',
    BLOCKED: 'bg-danger-400',
    DONE: 'bg-success-500',
    CANCELLED: 'bg-slate-300',
  }
  const PRIORITY_COLORS: Record<string, string> = {
    LOW: 'bg-slate-400',
    MEDIUM: 'bg-primary-400',
    HIGH: 'bg-warning-500',
    URGENT: 'bg-danger-500',
  }
  const PRIORITY_LABELS: Record<string, string> = {
    LOW: 'Bassa',
    MEDIUM: 'Media',
    HIGH: 'Alta',
    URGENT: 'Urgente',
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CheckSquare} label="Task Totali" value={total} hint="Nel periodo" tone="primary" />
        <StatCard icon={TrendingUp} label="Tasso Completamento" value={`${completionRate}%`} hint="Task completati vs totali" tone="success" />
        <StatCard icon={AlertTriangle} label="Scaduti" value={overdueCount} hint="Oltre la data di scadenza" tone="danger" />
        <StatCard icon={Clock} label="Completati" value={statusBreakdown['DONE'] ?? 0} hint="Task chiusi con successo" tone="neutral" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Distribuzione per Stato</h2>
          {Object.keys(statusBreakdown).length > 0 ? (
            <StatusBreakdown
              items={Object.entries(statusBreakdown).map(([k, v]) => ({
                label: STATUS_LABELS[k] ?? k,
                count: v as number,
                color: STATUS_COLORS[k] ?? 'bg-slate-400',
              }))}
            />
          ) : (
            <div className="flex items-center justify-center h-32 text-sm text-slate-400">Nessun dato disponibile</div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Distribuzione per Priorita'</h2>
          {Object.keys(priorityDist).length > 0 ? (
            <StatusBreakdown
              items={Object.entries(priorityDist).map(([k, v]) => ({
                label: PRIORITY_LABELS[k] ?? k,
                count: v as number,
                color: PRIORITY_COLORS[k] ?? 'bg-slate-400',
              }))}
            />
          ) : (
            <div className="flex items-center justify-center h-32 text-sm text-slate-400">Nessun dato disponibile</div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function ReportsPage() {
  const defaults = getDefaultDates()
  const [filters, setFilters] = useState<ReportFilters>({
    type: 'projects',
    startDate: defaults.startDate,
    endDate: defaults.endDate,
    clientId: '',
  })

  // Fetch clients for the filter dropdown
  const { data: clientsResponse } = useQuery({
    queryKey: ['clients', { limit: 100 }],
    queryFn: () => clientsApi.list({ limit: 100 }),
  })
  const clients: Client[] = (() => {
    const raw = (clientsResponse as any)?.data
    if (Array.isArray(raw)) return raw
    if (raw?.data && Array.isArray(raw.data)) return raw.data
    return []
  })()

  // Fetch report data
  const { data: reportData, isLoading, isError, error } = useQuery({
    queryKey: ['reports', filters],
    queryFn: () =>
      reportsApi.get({
        type: filters.type,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        clientId: filters.clientId || undefined,
      }),
    retry: 1,
    staleTime: 30_000,
  })

  // Extract inner data (handle { data: ... } wrapper)
  const data = (reportData as any)?.data ?? reportData ?? null

  function setFilter<K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  function handleTabKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, value: ReportType) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const idx = REPORT_TABS.findIndex((t) => t.value === value)
    const dir = e.key === 'ArrowRight' ? 1 : -1
    const nextIdx = (idx + dir + REPORT_TABS.length) % REPORT_TABS.length
    const next = REPORT_TABS[nextIdx]
    if (!next) return
    setFilter('type', next.value)
    const el = document.getElementById(`report-tab-${next.value}`)
    if (el instanceof HTMLElement) el.focus()
  }

  function resetPeriod() {
    const d = getDefaultDates()
    setFilters((prev) => ({ ...prev, startDate: d.startDate, endDate: d.endDate }))
  }

  const inputClass = cn(
    'px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg',
    'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
    'placeholder:text-slate-400 transition-colors'
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Report & Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">
            Analisi delle performance
          </p>
        </div>
      </div>

      {/* Report type tabs */}
      <div
        role="tablist"
        aria-label="Tipo di report"
        className="flex items-center gap-1 p-1 bg-slate-100 rounded-full w-fit max-w-full overflow-x-auto flex-nowrap whitespace-nowrap"
      >
        {REPORT_TABS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            role="tab"
            id={`report-tab-${value}`}
            aria-selected={filters.type === value}
            aria-controls={`report-panel-${value}`}
            tabIndex={filters.type === value ? 0 : -1}
            onClick={() => setFilter('type', value)}
            onKeyDown={(e) => handleTabKeyDown(e, value)}
            className={cn(
              'px-4 py-1.5 text-sm font-medium rounded-full transition-colors',
              filters.type === value
                ? 'bg-primary-600 text-white'
                : 'text-slate-600 hover:text-slate-900'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-3 flex-wrap bg-white border border-slate-200 rounded-2xl shadow-card p-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-medium text-slate-600">Periodo:</span>
        </div>
        <input
          type="date"
          value={filters.startDate}
          max={filters.endDate || undefined}
          onChange={(e) => setFilter('startDate', e.target.value)}
          className={inputClass}
          aria-label="Data inizio"
        />
        <span className="text-xs text-slate-400">—</span>
        <input
          type="date"
          value={filters.endDate}
          min={filters.startDate || undefined}
          onChange={(e) => setFilter('endDate', e.target.value)}
          className={inputClass}
          aria-label="Data fine"
        />
        <button
          type="button"
          onClick={resetPeriod}
          className="px-3.5 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
        >
          Reimposta periodo
        </button>
        <select
          value={filters.clientId}
          onChange={(e) => setFilter('clientId', e.target.value)}
          className={cn(inputClass, 'min-w-[180px]')}
          aria-label="Filtra per cliente"
        >
          <option value="">Tutti i clienti</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      <div
        role="tabpanel"
        id={`report-panel-${filters.type}`}
        aria-labelledby={`report-tab-${filters.type}`}
      >
        {isLoading ? (
          <LoadingSkeleton />
        ) : isError ? (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-8 text-center">
            <AlertTriangle className="w-8 h-8 text-danger-500 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-900 mb-1">Errore nel caricamento dei dati</p>
            <p className="text-sm text-slate-500">
              {error instanceof Error ? error.message : 'Controlla la connessione al server'}
            </p>
          </div>
        ) : data === null ? (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-8 text-center">
            <BarChart3 className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nessun dato disponibile per il periodo selezionato</p>
          </div>
        ) : filters.type === 'projects' ? (
          <ProjectsReport data={data} />
        ) : filters.type === 'clients' ? (
          <ClientsReport data={data} />
        ) : (
          <TasksReport data={data} />
        )}
      </div>
    </div>
  )
}
